import {
  HybridClock,
  materialize,
  type BookmarkChange,
  type BookmarkRow,
  type HlcState,
  type MaterializedTree,
  type PushResult,
  type RejectReason,
} from '@bsync/shared';
import { mergeChange } from './merge';
import { Mirror } from './mirror';
import { Outbox } from './outbox';
import { asRemoteError, type RemoteApi, type RemoteError } from './remote';
import type { KeyValueStore } from './storage';

export type ConflictKind =
  /** Another device changed the same field more recently; its value was kept. */
  | 'superseded'
  /** The server refused the change (e.g. a move that would create a cycle). */
  | 'rejected'
  /** The node was deleted elsewhere after this device last saw it. */
  | 'deleted_elsewhere'
  /** A deletion lost to a newer edit made elsewhere, so the node was kept. */
  | 'deletion_overridden';

export interface ConflictRecord {
  nodeId: string;
  title: string;
  kind: ConflictKind;
  field: 'content' | 'location' | 'deletion';
  reason?: RejectReason;
  byDevice: string | null;
  at: number;
}

export type SyncPhase = 'idle' | 'syncing' | 'offline' | 'error';

export interface SyncStatus {
  phase: SyncPhase;
  lastSyncAt: number | null;
  lastError: string | null;
  pending: number;
  conflicts: ConflictRecord[];
  revoked: boolean;
}

export interface Logger {
  debug(...args: unknown[]): void;
  warn(...args: unknown[]): void;
}

export const silentLogger: Logger = { debug() {}, warn() {} };

export interface SyncCoreOptions {
  deviceId: string;
  remote: RemoteApi;
  store: KeyValueStore;
  now?: () => number;
  pageSize?: number;
  pushBatchSize?: number;
  log?: Logger;
}

const MAX_CONFLICTS = 50;

export function describeError(e: RemoteError): string {
  switch (e.code) {
    case 'offline':
      return 'Offline — changes are saved and will sync when the connection returns.';
    case 'clock_skew':
      return "This device's clock is more than a day ahead. Fix the system time; changes are kept until then.";
    case 'device_revoked':
      return 'This device was disconnected from your account.';
    case 'unauthorized':
      return 'Your session expired. Sign in again to resume syncing.';
    case 'rate_limited':
      return 'The server is busy. Retrying shortly.';
    default:
      return e.message || 'Sync failed.';
  }
}

/**
 * Transport-agnostic replica: server mirror + durable outbox + clock.
 * The web dashboard uses it directly; browser extensions wrap it in
 * BrowserSync, which additionally reconciles with the native bookmark tree.
 */
export class SyncCore {
  readonly deviceId: string;
  readonly mirror: Mirror;
  readonly outbox: Outbox;
  clock!: HybridClock;
  private status: SyncStatus = {
    phase: 'idle',
    lastSyncAt: null,
    lastError: null,
    pending: 0,
    conflicts: [],
    revoked: false,
  };
  private listeners = new Set<(s: SyncStatus) => void>();
  private treeCache: { key: unknown; tree: MaterializedTree } | null = null;
  private version = 0;
  readonly now: () => number;
  readonly log: Logger;

  constructor(private readonly opts: SyncCoreOptions) {
    this.deviceId = opts.deviceId;
    this.mirror = new Mirror(opts.store);
    this.outbox = new Outbox(opts.store);
    this.now = opts.now ?? Date.now;
    this.log = opts.log ?? silentLogger;
  }

  get store(): KeyValueStore {
    return this.opts.store;
  }

  async init(): Promise<void> {
    await Promise.all([this.mirror.load(), this.outbox.load()]);
    const clock = await this.opts.store.get<HlcState>('clock');
    this.clock = new HybridClock(this.deviceId, clock, this.now);
    for (const r of this.mirror.rows.values()) this.observeRow(r);
    const saved = await this.opts.store.get<Pick<SyncStatus, 'conflicts' | 'lastSyncAt'>>('status');
    this.status = { ...this.status, ...saved, pending: this.outbox.size };
  }

  // ---------------------------------------------------------------- status

  getStatus(): SyncStatus {
    return this.status;
  }

  subscribe(fn: (s: SyncStatus) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  setStatus(patch: Partial<SyncStatus>): void {
    this.status = { ...this.status, ...patch, pending: this.outbox.size };
    for (const fn of this.listeners) fn(this.status);
  }

  async clearConflicts(): Promise<void> {
    this.setStatus({ conflicts: [] });
    await this.persistStatus();
  }

  private persistStatus() {
    return this.opts.store.set('status', { conflicts: this.status.conflicts, lastSyncAt: this.status.lastSyncAt });
  }

  // ---------------------------------------------------------------- clock

  tick(): string {
    return this.clock.tick();
  }

  private observeRow(r: BookmarkRow) {
    this.clock.observe(r.contentHlc);
    this.clock.observe(r.locationHlc);
    this.clock.observe(r.deletedHlc);
  }

  async persistClock(): Promise<void> {
    await this.opts.store.set('clock', this.clock.state());
  }

  // ---------------------------------------------------------------- local edits

  async stage(changes: BookmarkChange[]): Promise<void> {
    if (!changes.length) return;
    await this.outbox.stage(changes);
    await this.persistClock();
    this.version++;
    this.setStatus({});
  }

  /** Mirror rows with pending local changes applied optimistically. */
  optimisticRows(): Map<string, BookmarkRow> {
    const rows = new Map(this.mirror.rows);
    const now = new Date(this.now()).toISOString();
    for (const c of this.outbox.pending()) {
      const { row } = mergeChange(rows.get(c.id), c, { rev: 0, now, deviceId: this.deviceId });
      if (row) rows.set(c.id, row);
    }
    return rows;
  }

  /** Materialised optimistic tree, cached until the next change. */
  tree(): MaterializedTree {
    const key = this.version;
    if (this.treeCache?.key !== key) this.treeCache = { key, tree: materialize(this.optimisticRows()) };
    return this.treeCache.tree;
  }

  // ---------------------------------------------------------------- network

  /** Send every pending change. Throws RemoteError on transport failure. */
  async push(): Promise<{ pushed: number; changedIds: string[] }> {
    const batchSize = this.opts.pushBatchSize ?? 500;
    let pushed = 0;
    const changedIds: string[] = [];
    for (;;) {
      const batch = this.outbox.pending().slice(0, batchSize);
      if (!batch.length) break;
      let result: PushResult;
      try {
        result = await this.opts.remote.push(this.deviceId, batch);
      } catch (err) {
        throw this.handleRemoteError(err);
      }
      changedIds.push(...this.mirror.ingest(result.rows, false));
      for (const r of result.rows) this.observeRow(r);
      this.recordConflicts(batch, result);
      await this.outbox.acknowledge(batch);
      await this.mirror.flush();
      pushed += batch.length;
      this.version++;
      this.setStatus({});
    }
    return { pushed, changedIds };
  }

  /** Fetch everything newer than the cursor. Returns ids whose server state changed. */
  async pull(): Promise<string[]> {
    const pageSize = this.opts.pageSize ?? 1000;
    let state;
    try {
      state = await this.opts.remote.syncState(this.deviceId);
    } catch (err) {
      throw this.handleRemoteError(err);
    }
    const full = this.mirror.cursor > 0 && (state.gcRev > this.mirror.cursor || state.rev < this.mirror.cursor);
    if (!full && state.rev === this.mirror.cursor) return [];

    const fetched: BookmarkRow[] = [];
    let since = full ? 0 : this.mirror.cursor;
    for (;;) {
      let page: BookmarkRow[];
      try {
        page = await this.opts.remote.pull(since, pageSize);
      } catch (err) {
        throw this.handleRemoteError(err);
      }
      fetched.push(...page);
      if (page.length) since = page[page.length - 1]!.rev;
      if (page.length < pageSize) break;
    }
    for (const r of fetched) this.observeRow(r);

    let changed: string[];
    if (full) {
      // Tombstones may have been purged: rebuild the replica from scratch.
      const before = this.mirror.rows;
      this.mirror.replaceAll(fetched, Math.max(since, state.rev));
      changed = [...new Set([...before.keys(), ...fetched.map((r) => r.id)])];
      this.log.debug('full resync', fetched.length);
    } else {
      changed = this.mirror.ingest(fetched, true);
    }
    await this.mirror.flush();
    await this.persistClock();
    if (changed.length) this.version++;
    return changed;
  }

  /** Push then pull. The whole sync for clients without a native tree. */
  async sync(): Promise<string[]> {
    this.setStatus({ phase: 'syncing' });
    try {
      const { changedIds } = await this.push();
      const pulled = await this.pull();
      await this.markSynced();
      return [...changedIds, ...pulled];
    } catch (err) {
      this.markFailed(err);
      throw err;
    }
  }

  async markSynced(): Promise<void> {
    this.setStatus({ phase: 'idle', lastSyncAt: this.now(), lastError: null });
    await this.persistStatus();
  }

  markFailed(err: unknown): void {
    const e = asRemoteError(err);
    this.setStatus({
      phase: e.code === 'offline' ? 'offline' : 'error',
      lastError: describeError(e),
      revoked: e.code === 'device_revoked' || this.status.revoked,
    });
  }

  private handleRemoteError(err: unknown): RemoteError {
    return asRemoteError(err);
  }

  private recordConflicts(batch: BookmarkChange[], result: PushResult) {
    const rows = new Map(result.rows.map((r) => [r.id, r]));
    const rejected = new Map(result.rejected.map((r) => [r.id, r.reason]));
    const out: ConflictRecord[] = [];
    const at = this.now();
    for (const c of batch) {
      const r = rows.get(c.id);
      const title = r?.title || c.content?.title || '(untitled)';
      const by = r?.updatedBy && r.updatedBy !== this.deviceId ? r.updatedBy : null;
      const reason = rejected.get(c.id);
      if (reason) {
        out.push({ nodeId: c.id, title, kind: 'rejected', field: 'location', reason, byDevice: null, at });
        continue;
      }
      if (!r) continue;
      if (c.deletion?.deleted && !r.deleted && r.deletedHlc === c.deletion.hlc) {
        out.push({ nodeId: c.id, title, kind: 'deletion_overridden', field: 'deletion', byDevice: by, at });
        continue;
      }
      if ((c.content || c.location) && !c.deletion && r.deleted) {
        out.push({ nodeId: c.id, title, kind: 'deleted_elsewhere', field: c.content ? 'content' : 'location', byDevice: by, at });
        continue;
      }
      if (c.content && r.contentHlc > c.content.hlc) {
        out.push({ nodeId: c.id, title, kind: 'superseded', field: 'content', byDevice: by, at });
      } else if (c.location && r.locationHlc > c.location.hlc) {
        out.push({ nodeId: c.id, title, kind: 'superseded', field: 'location', byDevice: by, at });
      }
    }
    if (out.length) {
      this.status = { ...this.status, conflicts: [...out, ...this.status.conflicts].slice(0, MAX_CONFLICTS) };
      void this.persistStatus();
    }
  }

  async reset(): Promise<void> {
    await Promise.all([this.mirror.clear(), this.outbox.clear(), this.opts.store.remove('clock'), this.opts.store.remove('status')]);
    this.version++;
  }
}
